import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  ArrowLeft,
  Check,
  ChevronRight,
  Clock3,
  FileText,
  Images,
  MenuSquare,
  RefreshCcw,
  Send,
} from "lucide-react";

import {
  loadNovaWebsiteEditorDocuments,
  publishNovaWebsiteDocument,
  type NovaWebsiteEditorDocument,
} from "../content/nova-website-content";

import {
  loadNovaMediaSlots,
  publishNovaMediaSlot,
  type NovaMediaSlot,
} from "../media/nova-media";

type PublishingState = {
  website: NovaWebsiteEditorDocument[];
  media: NovaMediaSlot[];
};

function formatDate(
  value: string | null,
) {
  if (!value) {
    return "Not yet";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return value;
  }

  return new Intl.DateTimeFormat(
    "en-KE",
    {
      dateStyle: "medium",
      timeStyle: "short",
    },
  ).format(date);
}

function StatusPill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?:
    | "neutral"
    | "pending"
    | "live";
}) {
  const className =
    tone === "pending"
      ? "border-[#9d725c]/40 bg-[#ead8cc] text-[#7d513e]"
      : tone === "live"
        ? "border-[#718076]/30 bg-[#dfe3d7] text-[#314a3b]"
        : "border-[#b5bbae] bg-[#eee9dc] text-[#718076]";

  return (
    <span
      className={[
        "inline-flex border px-2.5 py-1 text-[8px] font-semibold uppercase tracking-[0.15em]",
        className,
      ].join(" ")}
    >
      {children}
    </span>
  );
}

function Metric({
  label,
  value,
  detail,
}: {
  label: string;
  value: number;
  detail: string;
}) {
  return (
    <div className="border border-[#b5bbae] bg-[#f6f1e8] p-5">
      <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#718076]">
        {label}
      </p>

      <p className="mt-2 font-['Cormorant_Garamond'] text-5xl leading-none text-[#314a3b]">
        {value}
      </p>

      <p className="mt-3 text-[10px] leading-5 text-[#718076]">
        {detail}
      </p>
    </div>
  );
}

function MediaPreview({
  slot,
  mode,
}: {
  slot: NovaMediaSlot;
  mode:
    | "draft"
    | "published";
}) {
  const url =
    mode === "draft"
      ? slot.draft_url
      : slot.published_url;

  const fileName =
    mode === "draft"
      ? slot.draft_file_name
      : slot.published_file_name;

  if (!url) {
    return (
      <div className="flex aspect-[4/3] items-center justify-center border border-dashed border-[#b5bbae] bg-[#eee9dc] px-4 text-center">
        <div>
          <Images
            size={17}
            className="mx-auto text-[#9d725c]"
          />

          <p className="mt-3 text-[9px] uppercase tracking-[0.14em] text-[#718076]">
            No {mode} media
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="relative aspect-[4/3] overflow-hidden bg-[#26392c]">
        {slot.media_kind ===
        "video" ? (
          <video
            src={url}
            muted
            playsInline
            controls
            className="h-full w-full object-cover"
          />
        ) : (
          <img
            src={url}
            alt=""
            className="h-full w-full object-cover"
          />
        )}
      </div>

      <p className="mt-2 truncate text-[9px] text-[#718076]">
        {fileName}
      </p>
    </div>
  );
}

export function NovaCmsPublishing() {
  const [
    state,
    setState,
  ] =
    useState<PublishingState>({
      website: [],
      media: [],
    });

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    busyKey,
    setBusyKey,
  ] =
    useState<string | null>(
      null,
    );

  const [
    message,
    setMessage,
  ] =
    useState<string | null>(
      null,
    );

  const [
    error,
    setError,
  ] =
    useState<string | null>(
      null,
    );

  const load =
    useCallback(
      async () => {
        setLoading(true);
        setError(null);

        try {
          const [
            website,
            media,
          ] =
            await Promise.all([
              loadNovaWebsiteEditorDocuments(),
              loadNovaMediaSlots(),
            ]);

          setState({
            website,
            media,
          });
        } catch (
          loadError
        ) {
          console.error(
            "[Nova CMS] publishing control room load failed",
            loadError,
          );

          setError(
            loadError instanceof Error
              ? loadError.message
              : "Publishing status could not be loaded.",
          );
        }

        setLoading(false);
      },
      [],
    );

  useEffect(() => {
    void load();
  }, [load]);

  const websiteDrafts =
    useMemo(
      () =>
        state.website.filter(
          (document) =>
            document.hasDraft,
        ),
      [state.website],
    );

  const mediaDrafts =
    useMemo(
      () =>
        state.media.filter(
          (slot) =>
            Boolean(
              slot.draft_path,
            ),
        ),
      [state.media],
    );

  const totalPending =
    websiteDrafts.length +
    mediaDrafts.length;

  const publishedWebsite =
    state.website.filter(
      (document) =>
        Boolean(
          document.published_at,
        ),
    ).length;

  const publishedMedia =
    state.media.filter(
      (slot) =>
        Boolean(
          slot.published_path,
        ),
    ).length;

  async function publishWebsite(
    document:
      NovaWebsiteEditorDocument,
  ) {
    setBusyKey(
      `website:${document.content_key}`,
    );

    setMessage(null);
    setError(null);

    try {
      await publishNovaWebsiteDocument(
        document.content_key,
      );

      await load();

      setMessage(
        `${document.label} is now live.`,
      );
    } catch (
      publishError
    ) {
      setError(
        publishError instanceof Error
          ? publishError.message
          : "Website content could not be published.",
      );
    }

    setBusyKey(null);
  }

  async function publishMedia(
    slot: NovaMediaSlot,
  ) {
    setBusyKey(
      `media:${slot.slot_key}`,
    );

    setMessage(null);
    setError(null);

    try {
      await publishNovaMediaSlot(
        slot,
      );

      await load();

      setMessage(
        `${slot.label} is now live.`,
      );
    } catch (
      publishError
    ) {
      setError(
        publishError instanceof Error
          ? publishError.message
          : "Media could not be published.",
      );
    }

    setBusyKey(null);
  }

  async function publishAllWebsite() {
    if (
      websiteDrafts.length ===
      0
    ) {
      return;
    }

    const confirmed =
      window.confirm(
        `Publish all ${websiteDrafts.length} website draft${websiteDrafts.length === 1 ? "" : "s"}?\n\nThese changes will become visible on the public website.`,
      );

    if (!confirmed) {
      return;
    }

    setBusyKey(
      "all:website",
    );

    setMessage(null);
    setError(null);

    try {
      for (
        const document of
        websiteDrafts
      ) {
        await publishNovaWebsiteDocument(
          document.content_key,
        );
      }

      await load();

      setMessage(
        `${websiteDrafts.length} website draft${websiteDrafts.length === 1 ? "" : "s"} published.`,
      );
    } catch (
      publishError
    ) {
      await load();

      setError(
        publishError instanceof Error
          ? publishError.message
          : "Website publishing stopped before all drafts were completed.",
      );
    }

    setBusyKey(null);
  }

  async function publishAllMedia() {
    if (
      mediaDrafts.length ===
      0
    ) {
      return;
    }

    const confirmed =
      window.confirm(
        `Publish all ${mediaDrafts.length} media draft${mediaDrafts.length === 1 ? "" : "s"}?\n\nEach draft will replace the currently published media for its slot.`,
      );

    if (!confirmed) {
      return;
    }

    setBusyKey(
      "all:media",
    );

    setMessage(null);
    setError(null);

    try {
      for (
        const slot of
        mediaDrafts
      ) {
        await publishNovaMediaSlot(
          slot,
        );
      }

      await load();

      setMessage(
        `${mediaDrafts.length} media draft${mediaDrafts.length === 1 ? "" : "s"} published.`,
      );
    } catch (
      publishError
    ) {
      await load();

      setError(
        publishError instanceof Error
          ? publishError.message
          : "Media publishing stopped before all drafts were completed.",
      );
    }

    setBusyKey(null);
  }

  async function publishEverything() {
    if (
      totalPending ===
      0
    ) {
      return;
    }

    const confirmed =
      window.confirm(
        `Publish everything pending?\n\n${websiteDrafts.length} website draft${websiteDrafts.length === 1 ? "" : "s"} and ${mediaDrafts.length} media draft${mediaDrafts.length === 1 ? "" : "s"} will go live.\n\nMenu changes are not included because Menu uses live-save.`,
      );

    if (!confirmed) {
      return;
    }

    setBusyKey(
      "all:everything",
    );

    setMessage(null);
    setError(null);

    let websitePublished =
      0;

    let mediaPublished =
      0;

    try {
      for (
        const document of
        websiteDrafts
      ) {
        await publishNovaWebsiteDocument(
          document.content_key,
        );

        websitePublished += 1;
      }

      for (
        const slot of
        mediaDrafts
      ) {
        await publishNovaMediaSlot(
          slot,
        );

        mediaPublished += 1;
      }

      await load();

      setMessage(
        `Publishing complete: ${websitePublished} website and ${mediaPublished} media change${mediaPublished === 1 ? "" : "s"} are now live.`,
      );
    } catch (
      publishError
    ) {
      await load();

      setError(
        `Publishing stopped after ${websitePublished} website and ${mediaPublished} media change${mediaPublished === 1 ? "" : "s"}. ${
          publishError instanceof Error
            ? publishError.message
            : "A publish operation failed."
        }`,
      );
    }

    setBusyKey(null);
  }

  const globallyBusy =
    busyKey !== null;

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <header className="border-b border-[#53685b] bg-[#26392c] text-[#f3ead7]">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-5 px-6 py-6 lg:px-10">
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => {
                window.location.href =
                  "/nova-hq";
              }}
              className="flex h-10 w-10 items-center justify-center border border-[#f3ead7]/20"
              aria-label="Back to Nova HQ"
            >
              <ArrowLeft
                size={16}
              />
            </button>

            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#d6bd91]">
                Nova HQ · Publishing
              </p>

              <h1 className="mt-1 font-['Cormorant_Garamond'] text-4xl font-light">
                Ready when you are.
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() =>
                void load()
              }
              disabled={
                loading ||
                globallyBusy
              }
              className="flex h-10 items-center gap-2 border border-[#f3ead7]/20 px-4 text-[9px] font-semibold uppercase tracking-[0.16em] disabled:opacity-40"
            >
              <RefreshCcw
                size={13}
              />
              Refresh
            </button>

            <button
              type="button"
              onClick={() =>
                void publishEverything()
              }
              disabled={
                globallyBusy ||
                totalPending ===
                  0
              }
              className="flex h-10 items-center gap-2 bg-[#d6bd91] px-4 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#26392c] disabled:opacity-40"
            >
              <Send
                size={13}
              />
              Publish everything
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1440px] px-6 py-8 lg:px-10">
        <section className="max-w-3xl">
          <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
            Publishing control room
          </p>

          <h2 className="mt-3 font-['Cormorant_Garamond'] text-5xl leading-[0.95] sm:text-6xl">
            What changes.
            <br />
            What goes live.
          </h2>

          <p className="mt-5 max-w-2xl text-sm leading-7 text-[#667067]">
            Website copy and media can be prepared privately before publishing. Review every pending change here before it reaches guests.
          </p>
        </section>

        {message && (
          <div className="mt-6 flex items-center gap-3 border border-[#718076]/30 bg-[#dfe3d7] px-5 py-4 text-xs">
            <Check
              size={15}
            />
            {message}
          </div>
        )}

        {error && (
          <div className="mt-6 border border-[#9d725c]/40 bg-[#ead8cc] px-5 py-4 text-xs leading-6 text-[#6e4635]">
            {error}
          </div>
        )}

        <section className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric
            label="Pending"
            value={
              totalPending
            }
            detail="Website and media changes waiting to go live."
          />

          <Metric
            label="Website drafts"
            value={
              websiteDrafts.length
            }
            detail={`${publishedWebsite} website documents currently have a published version.`}
          />

          <Metric
            label="Media drafts"
            value={
              mediaDrafts.length
            }
            detail={`${publishedMedia} media slots currently use a published asset.`}
          />

          <Metric
            label="Menu"
            value={0}
            detail="Menu uses live-save and does not wait in this publishing queue."
          />
        </section>

        {loading ? (
          <div className="mt-8 border border-[#b5bbae] bg-[#f6f1e8] p-10 text-sm text-[#718076]">
            Loading publishing status…
          </div>
        ) : (
          <>
            {totalPending ===
              0 && (
              <section className="mt-8 border border-[#718076]/30 bg-[#dfe3d7] p-7">
                <div className="flex items-start gap-4">
                  <Check
                    size={20}
                    className="mt-1 shrink-0"
                  />

                  <div>
                    <h3 className="font-['Cormorant_Garamond'] text-3xl">
                      Everything is published.
                    </h3>

                    <p className="mt-2 text-xs leading-6 text-[#667067]">
                      There are no saved website drafts or staged media files waiting to go live.
                    </p>
                  </div>
                </div>
              </section>
            )}

            <section className="mt-8 border border-[#b5bbae] bg-[#f6f1e8]">
              <div className="flex flex-wrap items-start justify-between gap-5 border-b border-[#b5bbae] p-6">
                <div>
                  <div className="flex items-center gap-3">
                    <FileText
                      size={17}
                      className="text-[#9d725c]"
                    />

                    <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                      Website
                    </p>
                  </div>

                  <h3 className="mt-3 font-['Cormorant_Garamond'] text-4xl">
                    Words waiting to go live.
                  </h3>

                  <p className="mt-2 text-xs leading-6 text-[#718076]">
                    Drafts remain private until they are published here or from the Website editor.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      window.location.href =
                        "/nova-hq/website";
                    }}
                    className="flex h-10 items-center gap-2 border border-[#b5bbae] px-4 text-[9px] font-semibold uppercase tracking-[0.15em]"
                  >
                    Open editor
                    <ChevronRight
                      size={12}
                    />
                  </button>

                  <button
                    type="button"
                    disabled={
                      globallyBusy ||
                      websiteDrafts.length ===
                        0
                    }
                    onClick={() =>
                      void publishAllWebsite()
                    }
                    className="flex h-10 items-center gap-2 bg-[#314a3b] px-4 text-[9px] font-semibold uppercase tracking-[0.15em] text-[#f3ead7] disabled:opacity-40"
                  >
                    <Send
                      size={12}
                    />
                    Publish all website
                  </button>
                </div>
              </div>

              <div>
                {state.website.map(
                  (document) => {
                    const pending =
                      document.hasDraft;

                    const itemBusy =
                      busyKey ===
                      `website:${document.content_key}`;

                    return (
                      <div
                        key={
                          document.content_key
                        }
                        className={[
                          "grid gap-5 border-b border-[#b5bbae]/50 p-5 last:border-b-0 lg:grid-cols-[minmax(0,1fr)_190px_190px_auto]",
                          pending
                            ? "bg-[#fffaf0]"
                            : "",
                        ].join(" ")}
                      >
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-['Cormorant_Garamond'] text-2xl leading-none">
                              {
                                document.label
                              }
                            </p>

                            <StatusPill
                              tone={
                                pending
                                  ? "pending"
                                  : "live"
                              }
                            >
                              {pending
                                ? "Draft pending"
                                : "Live"}
                            </StatusPill>
                          </div>

                          <p className="mt-2 font-mono text-[8px] text-[#718076]">
                            {
                              document.content_key
                            }
                          </p>
                        </div>

                        <div>
                          <p className="text-[8px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                            Draft updated
                          </p>

                          <p className="mt-2 text-[10px] leading-5">
                            {pending
                              ? formatDate(
                                  document.updated_at,
                                )
                              : "No pending draft"}
                          </p>
                        </div>

                        <div>
                          <p className="text-[8px] font-semibold uppercase tracking-[0.15em] text-[#718076]">
                            Published
                          </p>

                          <p className="mt-2 text-[10px] leading-5">
                            {formatDate(
                              document.published_at,
                            )}
                          </p>
                        </div>

                        <div className="flex items-center justify-start lg:justify-end">
                          {pending ? (
                            <button
                              type="button"
                              disabled={
                                globallyBusy
                              }
                              onClick={() =>
                                void publishWebsite(
                                  document,
                                )
                              }
                              className="flex h-10 min-w-[112px] items-center justify-center gap-2 bg-[#314a3b] px-4 text-[8px] font-semibold uppercase tracking-[0.15em] text-[#f3ead7] disabled:opacity-40"
                            >
                              <Send
                                size={11}
                              />
                              {itemBusy
                                ? "Publishing…"
                                : "Publish"}
                            </button>
                          ) : (
                            <StatusPill>
                              No action
                            </StatusPill>
                          )}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </section>

            <section className="mt-8 border border-[#b5bbae] bg-[#f6f1e8]">
              <div className="flex flex-wrap items-start justify-between gap-5 border-b border-[#b5bbae] p-6">
                <div>
                  <div className="flex items-center gap-3">
                    <Images
                      size={17}
                      className="text-[#9d725c]"
                    />

                    <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                      Media
                    </p>
                  </div>

                  <h3 className="mt-3 font-['Cormorant_Garamond'] text-4xl">
                    Images waiting in private.
                  </h3>

                  <p className="mt-2 max-w-2xl text-xs leading-6 text-[#718076]">
                    Draft media remains in private storage until published. Publishing replaces that slot's live asset using the existing safe storage lifecycle.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      window.location.href =
                        "/nova-hq/media";
                    }}
                    className="flex h-10 items-center gap-2 border border-[#b5bbae] px-4 text-[9px] font-semibold uppercase tracking-[0.15em]"
                  >
                    Open media
                    <ChevronRight
                      size={12}
                    />
                  </button>

                  <button
                    type="button"
                    disabled={
                      globallyBusy ||
                      mediaDrafts.length ===
                        0
                    }
                    onClick={() =>
                      void publishAllMedia()
                    }
                    className="flex h-10 items-center gap-2 bg-[#314a3b] px-4 text-[9px] font-semibold uppercase tracking-[0.15em] text-[#f3ead7] disabled:opacity-40"
                  >
                    <Send
                      size={12}
                    />
                    Publish all media
                  </button>
                </div>
              </div>

              <div className="grid gap-px bg-[#b5bbae] md:grid-cols-2 xl:grid-cols-3">
                {state.media.map(
                  (slot) => {
                    const pending =
                      Boolean(
                        slot.draft_path,
                      );

                    const itemBusy =
                      busyKey ===
                      `media:${slot.slot_key}`;

                    return (
                      <article
                        key={
                          slot.slot_key
                        }
                        className="bg-[#f6f1e8] p-5"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-[8px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                              {
                                slot.section_key
                              }
                            </p>

                            <h4 className="mt-2 font-['Cormorant_Garamond'] text-2xl leading-none">
                              {
                                slot.label
                              }
                            </h4>

                            <p className="mt-2 font-mono text-[8px] text-[#718076]">
                              {
                                slot.slot_key
                              }
                            </p>
                          </div>

                          <StatusPill
                            tone={
                              pending
                                ? "pending"
                                : slot.published_path
                                  ? "live"
                                  : "neutral"
                            }
                          >
                            {pending
                              ? "Draft pending"
                              : slot.published_path
                                ? "Live"
                                : "Fallback"}
                          </StatusPill>
                        </div>

                        <div className="mt-5 grid grid-cols-2 gap-3">
                          <div>
                            <p className="mb-2 text-[8px] font-semibold uppercase tracking-[0.14em] text-[#718076]">
                              Live
                            </p>

                            <MediaPreview
                              slot={slot}
                              mode="published"
                            />
                          </div>

                          <div>
                            <p className="mb-2 text-[8px] font-semibold uppercase tracking-[0.14em] text-[#718076]">
                              Draft
                            </p>

                            <MediaPreview
                              slot={slot}
                              mode="draft"
                            />
                          </div>
                        </div>

                        <div className="mt-5 grid grid-cols-2 gap-3 border-t border-[#b5bbae]/50 pt-4">
                          <div>
                            <div className="flex items-center gap-1.5 text-[8px] font-semibold uppercase tracking-[0.13em] text-[#718076]">
                              <Clock3
                                size={10}
                              />
                              Draft
                            </div>

                            <p className="mt-2 text-[9px] leading-5">
                              {formatDate(
                                slot.draft_updated_at,
                              )}
                            </p>
                          </div>

                          <div>
                            <div className="flex items-center gap-1.5 text-[8px] font-semibold uppercase tracking-[0.13em] text-[#718076]">
                              <Clock3
                                size={10}
                              />
                              Live
                            </div>

                            <p className="mt-2 text-[9px] leading-5">
                              {formatDate(
                                slot.published_at,
                              )}
                            </p>
                          </div>
                        </div>

                        {pending && (
                          <button
                            type="button"
                            disabled={
                              globallyBusy
                            }
                            onClick={() =>
                              void publishMedia(
                                slot,
                              )
                            }
                            className="mt-5 flex h-10 w-full items-center justify-center gap-2 bg-[#314a3b] px-4 text-[8px] font-semibold uppercase tracking-[0.15em] text-[#f3ead7] disabled:opacity-40"
                          >
                            <Send
                              size={11}
                            />
                            {itemBusy
                              ? "Publishing…"
                              : "Publish media"}
                          </button>
                        )}
                      </article>
                    );
                  },
                )}
              </div>
            </section>

            <section className="mt-8 border border-[#b5bbae] bg-[#f6f1e8] p-6">
              <div className="flex flex-wrap items-start justify-between gap-6">
                <div className="flex max-w-3xl gap-4">
                  <MenuSquare
                    size={20}
                    className="mt-1 shrink-0 text-[#9d725c]"
                  />

                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-['Cormorant_Garamond'] text-3xl">
                        Menu publishes differently.
                      </h3>

                      <StatusPill
                        tone="live"
                      >
                        Live save
                      </StatusPill>
                    </div>

                    <p className="mt-3 text-xs leading-6 text-[#667067]">
                      Menu changes are written directly to the canonical Nova catalogue. Published and visible items are then read by the Website and QR Menu from Supabase. They do not wait in this draft queue.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    window.location.href =
                      "/nova-hq/menu";
                  }}
                  className="flex h-10 items-center gap-2 border border-[#314a3b] px-4 text-[9px] font-semibold uppercase tracking-[0.15em]"
                >
                  Open menu
                  <ChevronRight
                    size={12}
                  />
                </button>
              </div>
            </section>

            <section className="mb-8 mt-8 bg-[#26392c] p-7 text-[#f3ead7] sm:p-9">
              <div className="flex flex-wrap items-end justify-between gap-7">
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#d6bd91]">
                    Final release
                  </p>

                  <h3 className="mt-3 font-['Cormorant_Garamond'] text-4xl">
                    {totalPending ===
                    0
                      ? "Nothing waiting."
                      : `${totalPending} change${totalPending === 1 ? "" : "s"} waiting.`}
                  </h3>

                  <p className="mt-3 max-w-xl text-xs leading-6 text-[#f3ead7]/65">
                    Publish Everything promotes every currently saved Website draft and every currently staged Media draft. Menu is intentionally excluded because it uses live-save.
                  </p>
                </div>

                <button
                  type="button"
                  disabled={
                    globallyBusy ||
                    totalPending ===
                      0
                  }
                  onClick={() =>
                    void publishEverything()
                  }
                  className="flex h-12 items-center gap-2 bg-[#d6bd91] px-6 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#26392c] disabled:opacity-40"
                >
                  <Send
                    size={13}
                  />

                  {busyKey ===
                  "all:everything"
                    ? "Publishing…"
                    : "Publish everything"}
                </button>
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
